import { Answer, AnswerType, ImageView, LogoView } from '@packages/types'
import classNames from 'classnames'
import React from 'react'

import { useDispatch } from 'customizer/hooks'
import { setMobileDescription } from 'customizer/questionPanel/actions'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import defaultThumbnailImg from 'img/variation.png'
import ColorAnswer from 'themes/common/components/answers/ColorAnswer'
import SVGAnswer from 'themes/common/components/answers/SVGAnswer'
import ThumbnailBadge from 'themes/common/components/answers/ThumbnailBadge'
import ExtraPrice from 'themes/common/components/ExtraPrice'
import ExtraPriceLabel from 'themes/common/components/ExtraPriceLabel'
import type { AnswerStyle } from 'themes/common/types/style'

import DescriptionButton from '../DescriptionButton'
import ImageAnswer from './ImageAnswer'
import NameLabel from './NameLabel'

import './ThumbnailAnswer.scss'

const FLOATING_LABEL_THUMBNAIL_HEIGHT_THRESHOLD = 84

type IconProps = ThumbnailAnswerProps & {
  answerStyle: AnswerStyle
}

const AnswerIcon = (props: IconProps) => {
  const { type, thumbnail, answerStyle } = props

  if (thumbnail != null) {
    return <ImageAnswer {...props} value={thumbnail.url || defaultThumbnailImg} answerStyle={answerStyle} />
  }

  switch (type) {
    case AnswerType.Image:
      const imageView = (props.views as ImageView[]).find(({ image }) => image != null)
      return (
        <ImageAnswer
          {...props}
          value={imageView?.image?.url ?? defaultThumbnailImg}
          noResize={!imageView?.image?.url}
          answerStyle={answerStyle}
        />
      )
    case AnswerType.Logo:
      const logoView = (props.views as LogoView[]).find(({ logo }) => logo != null)

      return (
        <ImageAnswer
          {...props}
          value={logoView?.logo?.url ?? defaultThumbnailImg}
          noResize={!logoView?.logo?.url}
          answerStyle={answerStyle}
        />
      )
    case AnswerType.Material:
    case AnswerType.FontSize:
    case AnswerType.Font:
    case AnswerType.Value:
      return <SVGAnswer {...props} answerStyle={answerStyle} />
    case AnswerType.Color:
    case AnswerType.Outline:
      return <ColorAnswer {...props} answerStyle={answerStyle} />
    case AnswerType.Text:
      return <ImageAnswer {...props} value={defaultThumbnailImg} noResize answerStyle={answerStyle} />
    default:
      return null
  }
}

export type ThumbnailAnswerProps = Answer & {
  isLarge: boolean
  label?: string
  currency: string
  lang?: string
  country?: string
  extraClassNames?: string
  handleClick: (answerId: string) => void
  selected: boolean
  disabled?: boolean
  showLabel: boolean
  price?: string | number
  isMultiAnswer?: boolean
  isOutOfStock?: boolean
  showRelativePrice?: boolean
}

const ThumbnailAnswer = (props: ThumbnailAnswerProps) => {
  const {
    id,
    selected,
    disabled,
    handleClick = () => {},
    showLabel,
    price,
    currency,
    lang,
    country,
    isLarge,
    isMultiAnswer,
    isOutOfStock,
    showRelativePrice,
  } = props
  const dispatch = useDispatch()

  const answerStyle = useThemeSettings('answer')
  const thumbnailStyle = useThemeSettings(isLarge ? 'largeThumbnailsButton' : 'thumbnailsButton')

  const style = {
    ...answerStyle,
    borderRadius: thumbnailStyle.mobileThumbnailBorderRadius,
    width: thumbnailStyle.mobileThumbnailWidth,
    height: thumbnailStyle.mobileThumbnailHeight,
  }

  const { backgroundColor } = useThemeSettings('questionPanel')

  const floatingLabel =
    showLabel && +thumbnailStyle.mobileThumbnailHeight.replace('px', '') > FLOATING_LABEL_THUMBNAIL_HEIGHT_THRESHOLD

  return (
    <div
      className={classNames('answer-container', {
        selected: selected,
        disabled: disabled,
        'answer-container--with-label': showLabel,
        'answer-container--with-floating-label': floatingLabel,
      })}
      style={{
        marginRight: showLabel && !floatingLabel ? '4px' : thumbnailStyle.mobileThumbnailMargin,
      }}
      onClick={() => !disabled && handleClick(id)}
      data-testid="thumbnail-answer"
    >
      <div className="answer-container__wrapper" style={style}>
        <ThumbnailBadge
          isLarge={isLarge}
          selected={selected}
          isOutOfStock={isOutOfStock}
          isMultiAnswer={isMultiAnswer && thumbnailStyle.showMultiAnswerFeedback}
        />
        <AnswerIcon {...props} answerStyle={style} />
        {props.showDescription && (
          <DescriptionButton
            isVisible={false}
            onClick={() => dispatch(setMobileDescription(props))}
            className={classNames('description-button__thumbnail', { 'description-button__thumbnail--large': isLarge })}
          />
        )}
      </div>

      {!(showLabel && floatingLabel) && (
        <ExtraPriceLabel
          extraClassNames="price-label__under-thumbnail"
          price={price}
          currency={currency}
          lang={lang}
          country={country}
          showRelativePrice={showRelativePrice}
        />
      )}
      {showLabel && (
        <NameLabel
          {...props}
          extraPrice={
            floatingLabel && (
              <ExtraPrice
                price={price}
                currency={currency}
                lang={lang}
                country={country}
                includeSign={showRelativePrice}
              />
            )
          }
          answerStyle={{
            ...answerStyle,
            width: `calc(${thumbnailStyle.mobileThumbnailWidth} + ${thumbnailStyle.mobileThumbnailMargin} - 4px)`,
            maxWidth: `calc(${thumbnailStyle.mobileThumbnailWidth} + ${thumbnailStyle.mobileThumbnailMargin} - 4px)`,
            backgroundColor: `${backgroundColor}DE`,
            minHeight: answerStyle.answerFontSize,
          }}
        />
      )}
    </div>
  )
}

export default ThumbnailAnswer
